type WebSocketEventType = 
  | 'timer:started'
  | 'timer:paused'
  | 'timer:resumed'
  | 'timer:stopped'
  | 'timer:cancelled'
  | 'timer:tick'
  | 'entry:created'
  | 'entry:updated'
  | 'entry:deleted'
  | 'team:member_status'
  | 'team:timer_update'
  | 'connection:established'
  | 'connection:closed'
  | 'connection:error'

type WebSocketEventHandler = (data: unknown) => void

interface WebSocketServiceOptions {
  url: string
  token: string
  reconnectAttempts?: number
  reconnectDelay?: number
  maxReconnectDelay?: number
  heartbeatInterval?: number
}

class WebSocketService {
  private ws: WebSocket | null = null
  private url: string
  private token: string
  private reconnectAttempts: number
  private currentReconnectAttempt = 0
  private reconnectDelay: number
  private maxReconnectDelay: number
  private heartbeatInterval: number
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private eventHandlers: Map<WebSocketEventType, Set<WebSocketEventHandler>> = new Map()
  private isIntentionallyClosed = false
  private connectionState: 'connecting' | 'connected' | 'disconnected' | 'reconnecting' = 'disconnected'

  constructor(options: WebSocketServiceOptions) {
    this.url = options.url
    this.token = options.token
    this.reconnectAttempts = options.reconnectAttempts ?? 10
    this.reconnectDelay = options.reconnectDelay ?? 1000
    this.maxReconnectDelay = options.maxReconnectDelay ?? 30000
    this.heartbeatInterval = options.heartbeatInterval ?? 30000
  }

  private authenticated = false

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return
    }

    this.isIntentionallyClosed = false
    this.connectionState = 'connecting'
    this.authenticated = false

    try {
      this.ws = new WebSocket(this.url)

      this.ws.onopen = () => {
        // Send authentication as first message (handshake)
        this.send('auth', { token: this.token })
      }

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          if (message.type === 'auth:success' && !this.authenticated) {
            this.authenticated = true
            this.connectionState = 'connected'
            this.currentReconnectAttempt = 0
            this.startHeartbeat()
            this.emit('connection:established', { timestamp: Date.now() })
            return
          }
          if (message.type === 'auth:error' && !this.authenticated) {
            this.connectionState = 'disconnected'
            this.emit('connection:error', { error: new Error('Authentication failed'), timestamp: Date.now() })
            this.ws?.close()
            return
          }
          if (this.authenticated && message.type) {
            this.emit(message.type as WebSocketEventType, message.data)
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      this.ws.onclose = () => {
        this.connectionState = 'disconnected'
        this.authenticated = false
        this.stopHeartbeat()
        this.emit('connection:closed', { timestamp: Date.now() })

        if (!this.isIntentionallyClosed) {
          this.scheduleReconnect()
        }
      }

      this.ws.onerror = (error) => {
        this.connectionState = 'disconnected'
        this.emit('connection:error', { error, timestamp: Date.now() })
      }
    } catch (error) {
      this.connectionState = 'disconnected'
      this.emit('connection:error', { error, timestamp: Date.now() })
      this.scheduleReconnect()
    }
  }

  disconnect(): void {
    this.isIntentionallyClosed = true
    this.stopHeartbeat()
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    
    this.connectionState = 'disconnected'
  }

  private scheduleReconnect(): void {
    if (this.currentReconnectAttempt >= this.reconnectAttempts) {
      this.emit('connection:error', { 
        message: 'Max reconnection attempts reached',
        timestamp: Date.now() 
      })
      return
    }

    this.connectionState = 'reconnecting'
    this.currentReconnectAttempt++
    
    // Exponential backoff with jitter
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.currentReconnectAttempt - 1),
      this.maxReconnectDelay
    ) * (0.8 + Math.random() * 0.4) // Add 20% jitter

    this.reconnectTimer = setTimeout(() => {
      this.connect()
    }, delay)
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }))
      }
    }, this.heartbeatInterval)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  send(type: string, data?: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, data, timestamp: Date.now() }))
    } else {
      console.warn('WebSocket is not connected. Message not sent:', { type, data })
    }
  }

  on(event: WebSocketEventType, handler: WebSocketEventHandler): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set())
    }
    this.eventHandlers.get(event)!.add(handler)

    // Return unsubscribe function
    return () => {
      this.eventHandlers.get(event)?.delete(handler)
    }
  }

  off(event: WebSocketEventType, handler: WebSocketEventHandler): void {
    this.eventHandlers.get(event)?.delete(handler)
  }

  private emit(event: WebSocketEventType, data: unknown): void {
    this.eventHandlers.get(event)?.forEach(handler => {
      try {
        handler(data)
      } catch (error) {
        console.error(`Error in WebSocket event handler for ${event}:`, error)
      }
    })
  }

  getConnectionState(): 'connecting' | 'connected' | 'disconnected' | 'reconnecting' {
    return this.connectionState
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }
}

let globalWebSocketService: WebSocketService | null = null

export function initializeWebSocket(url: string, token: string): WebSocketService {
  if (globalWebSocketService) {
    globalWebSocketService.disconnect()
  }
  
  globalWebSocketService = new WebSocketService({ url, token })
  return globalWebSocketService
}

export function getWebSocketService(): WebSocketService | null {
  return globalWebSocketService
}

export function disconnectWebSocket(): void {
  globalWebSocketService?.disconnect()
  globalWebSocketService = null
}

export type { WebSocketEventType, WebSocketEventHandler, WebSocketServiceOptions }
export { WebSocketService }
